import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  Heading,
  VStack,
  Text,
  Alert,
  AlertIcon,
  HStack,
  PinInput,
  PinInputField,
  Spinner,
} from "@chakra-ui/react";
import { useAuthStore } from "@/store/auth";
import { TwoFactorService } from "@/lib/services/twoFactorService";
import { z } from "zod";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const twoFactorSchema = z.object({
  code: z
    .string()
    .min(4, "Code must be 4 digits")
    .max(4, "Code must be 4 digits")
    .regex(/^\d{4}$/, "Code must contain only numbers"),
});

type TwoFactorFormData = z.infer<typeof twoFactorSchema>;

export function TwoFactorForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [showResend, setShowResend] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [attemptCount, setAttemptCount] = useState(0);
  const [maxAttempts] = useState(3);
  const [canResend, setCanResend] = useState(true);
  const [otp, setOtp] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyTwoFactor } = useAuthStore();
  const { settings: siteSettings } = useSiteSettings();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TwoFactorFormData>({
    resolver: zodResolver(twoFactorSchema),
  });

  useEffect(() => {
    const email = searchParams.get("email");
    if (email) {
      setUserEmail(email);
      setAttemptCount(0);
      setCanResend(true);
      setCountdown(60);
      setShowResend(false);
    }
  }, [searchParams]);

  // Countdown timer for showing Resend
  useEffect(() => {
    if (countdown <= 0) {
      setShowResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (value: string) => {
    setOtp(value);
    setValue("code", value);
    if (value.length === 4) handleOtpComplete(value);
  };

  const handleOtpComplete = async (value: string) => {
    try {
      setIsLoading(true);
      setError("");
      setMessage("");

      const rememberMe = searchParams.get("remember_me") === "1";
      const result = await verifyTwoFactor(userEmail, value, rememberMe);

      if (result.success) {
        navigate("/dashboard", { replace: true });
      } else {
        setError(result.message || "Invalid verification code.");
        setOtp("");
        handleFailedAttempt();
      }
    } catch {
      setError("Invalid verification code.");
      setOtp("");
      handleFailedAttempt();
    } finally {
      setIsLoading(false);
    }
  };

  const handleFailedAttempt = () => {
    const newCount = attemptCount + 1;
    setAttemptCount(newCount);

    if (newCount >= maxAttempts) {
      setCanResend(false);
      setCountdown(60);
    }
  };

  // Submit handler for clicking verify button
  const onSubmit = async (data: TwoFactorFormData) => {
    try {
      twoFactorSchema.parse(data);
      await handleOtpComplete(data.code);
    } catch (err: any) {
      if (err.errors && err.errors.length > 0) {
        setError(err.errors[0].message);
      } else {
        setError("Invalid code");
      }
    }
  };

  // Resend code
  const handleResendCode = async () => {
    if (!canResend) return;

    try {
      setResendLoading(true);
      setError("");
      setMessage("");

      const result = await TwoFactorService.sendCode(userEmail);

      if (result.success) {
        setMessage(result.message || "Verification code sent again!");
        setCountdown(60);
        setShowResend(false);
      } else {
        setError(result.message || "Failed to resend code.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to resend code.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgImage="url('/assets/img/bg.jpg')"
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
      px={{ base: 4, sm: 6 }}
    >
      <Card
        w="full"
        maxW="448px"
        borderRadius="12px"
        boxShadow="0 0 1px 0 rgba(0,0,0,0.04), 0 2px 6px 0 rgba(0,0,0,0.04)"
        bg="#FFFFFF"
      >
        <CardBody p="40px" gap="41px">
          <VStack spacing={{ base: 4, md: 6 }}>
            {/* Logo */}
            <Box textAlign="center" mb={{ base: 1, md: 2 }}>
              <img
                src={siteSettings.auth_logo || "/assets/img/logo.jpg"}
                alt="Logo"
                style={{
                  maxWidth: "220px",
                  height: "66px",
                  width: "100%",
                  margin: "0 auto",
                  opacity: 0.9,
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Heading */}
            <VStack spacing={2}>
              <Heading
                size={{ base: "md", md: "md" }}
                fontWeight="700"
                color="#0A1B39"
              >
                OTP Verification
              </Heading>
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="#6C7688"
                fontWeight="400"
                textAlign="center"
                px={{ base: 2, sm: 0 }}
              >
                Please check your email for the OTP
              </Text>
            </VStack>

            {message && (
              <Alert
                status="success"
                borderRadius="md"
                fontSize={{ base: "xs", sm: "sm" }}
              >
                <AlertIcon />
                {message}
              </Alert>
            )}

            {error && (
              <Alert
                status="error"
                borderRadius="md"
                fontSize={{ base: "xs", sm: "sm" }}
              >
                <AlertIcon />
                {error}
              </Alert>
            )}

            {/* OTP */}
            <FormControl isInvalid={!!errors.code}>
              <HStack justify="center" spacing={{ base: 2, sm: 3 }} mt={2}>
                <PinInput
                  otp
                  value={otp}
                  onChange={handleOtpChange}
                  size={{ base: "md", md: "lg" }}
                  placeholder="0"
                >
                  <PinInputField
                    borderColor="#E7E8EB"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "orange.500",
                      boxShadow: "0 0 0 1px #ed8936",
                    }}
                    borderRadius="8px"
                    fontSize={{ base: "lg", md: "sm" }}
                    w={{ base: "45px", sm: "34px", md: "56px" }}
                    h={{ base: "45px", sm: "36px", md: "56px" }}
                  />
                  <PinInputField
                    borderColor="#E7E8EB"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "orange.500",
                      boxShadow: "0 0 0 1px #ed8936",
                    }}
                    borderRadius="8px"
                    fontSize={{ base: "lg", md: "sm" }}
                    w={{ base: "45px", sm: "34px", md: "56px" }}
                    h={{ base: "45px", sm: "36px", md: "56px" }}
                  />
                  <PinInputField
                    borderColor="#E7E8EB"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "orange.500",
                      boxShadow: "0 0 0 1px #ed8936",
                    }}
                    borderRadius="8px"
                    fontSize={{ base: "lg", md: "sm" }}
                    w={{ base: "45px", sm: "34px", md: "56px" }}
                    h={{ base: "45px", sm: "36px", md: "56px" }}
                  />
                  <PinInputField
                    borderColor="#E7E8EB"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "orange.500",
                      boxShadow: "0 0 0 1px #ed8936",
                    }}
                    borderRadius="8px"
                    fontSize={{ base: "lg", md: "sm" }}
                    w={{ base: "45px", sm: "34px", md: "56px" }}
                    h={{ base: "45px", sm: "36px", md: "56px" }}
                  />
                </PinInput>
              </HStack>

              <FormErrorMessage justifyContent="center" mt={2} fontSize="xs">
                {errors.code?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Timer Display */}
            {countdown > 0 && !showResend && (
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="#6C7688"
                textAlign="center"
                fontWeight="400"
              >
                00:{countdown.toString().padStart(2, "0")}
              </Text>
            )}

            {/* Resend */}
            {showResend && (
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color="gray.600"
                textAlign="center"
              >
                Didn't receive?{" "}
                <Button
                  as="span"
                  variant="link"
                  fontSize={{ base: "xs", sm: "sm" }}
                  color="#DC3611"
                  fontWeight="700"
                  onClick={handleResendCode}
                  isDisabled={!canResend || resendLoading}
                  px={0}
                  ml={1}
                  _hover={{ color: "#c92f0e", textDecoration: "underline" }}
                >
                  {resendLoading ? (
                    <Box display="inline-flex" alignItems="center" gap={1}>
                      <Spinner size="xs" />
                      <Text as="span">Resending...</Text>
                    </Box>
                  ) : (
                    "Resend"
                  )}
                </Button>
              </Text>
            )}

            {/* Verify Button */}
            <Button
              type="button"
              w="full"
              borderRadius="10px"
              background="linear-gradient(90deg, #dc3611 0%, #fa886f 100%)"
              color="white"
              size={{ base: "md", md: "lg" }}
              fontSize={{ base: "sm", md: "md" }}
              fontWeight="500"
              _hover={{
                background: "linear-gradient(90deg, #c92f0e 0%, #f97960 100%)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(220, 54, 17, 0.3)",
              }}
              _active={{
                background: "linear-gradient(90deg, #b32a0d 0%, #f86951 100%)",
                transform: "translateY(0)",
              }}
              isDisabled={isLoading}
              onClick={handleSubmit(onSubmit)}
              transition="all 0.2s"
            >
              {isLoading ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <Spinner size="sm" />
                  <Text>Verifying...</Text>
                </Box>
              ) : (
                "Verify"
              )}
            </Button>
          </VStack>
        </CardBody>
      </Card>
      <Text
        fontSize={{ base: "11px", sm: "14px" }}
        color="#0A1B39"
        position="fixed"
        bottom={{ base: "50px", md: "80px" }}
        fontWeight="400"
        textAlign="center"
        px={4}
      >
        Copyright ©2025 – ActionLabs
      </Text>
    </Box>
  );
}
